// AB#1871 / AB#1872 — BLOCKING "Deactivate category" confirmation modal
// (renamed from ArchiveCategoryModal).
//
// On open we fetch GET /admin/{id}/impact and show the 3 impact COUNTS
// (N salons / M services / K upcoming bookings) plus the single-category
// warning. AB#1871: each count is an EXPANDABLE ACCORDION whose detail list
// lazy-fetches on first expand (skip/take, "+ N more" pagination):
//   - Subscribed salons → name · city · "only category" flag
//   - Services          → name · salon · "From {price} {JOD|AED}"
//   - Upcoming bookings → PDPL-SAFE: date+time · service · salon ONLY
//                          (NO customer identity — the API returns none)
//
// Weight preservation (decision 6): despite the softer "Deactivate" verb the
// dialog keeps full ceremony — impact counts + drill-down + the reversibility
// body copy + a destructive-red confirm. Confirm → POST /admin/{id}/deactivate.
// The confirm button stays disabled until the impact counts have loaded so the
// SuperAdmin always confirms with the numbers visible.

import React, { useState } from 'react';
import Button from '../../../components/Button/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
// AB#2052 — wall-clock (UTC) appointment datetime formatting.
import { formatAppointmentDateTime } from '../../../lib/appointmentTime';
import {
    useDeactivateCategory,
    useCategoryImpact,
    useCategoryImpactSalons,
    useCategoryImpactServices,
    useCategoryImpactBookings,
} from '../../../hooks/useCategoriesAdmin';
import type {
    CategoryImpactSalonRow,
    CategoryImpactServiceRow,
    CategoryImpactBookingRow,
} from '../../../types/api-responses';
import styles from './CategoryModals.module.css';

interface DeactivateCategoryModalProps {
    open: boolean;
    onClose: () => void;
    categoryId: number | null;
    categoryName: string;
}

/** Wrap a count/number in an LTR isolate so digits stay LTR inside RTL copy. */
const Count: React.FC<{ value: number }> = ({ value }) => (
    <span dir="ltr" className={styles.impactCount}>
        {value}
    </span>
);

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
    <span
        aria-hidden="true"
        className={`${styles.accordionChevron} ${open ? styles.accordionChevronOpen : ''}`}
    >
        ▾
    </span>
);

type DrillResult<T> = {
    items: T[];
    total: number;
    isLoading: boolean;
    isError: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    refetch: () => void;
};

/**
 * Shared accordion section. `aria-expanded` is wired on the disclosure button
 * (committee MAJOR — all three accordions). The body lazy-renders only when
 * expanded; the underlying query is `enabled` only while expanded.
 */
function ImpactAccordion<T>({
    id,
    label,
    count,
    expanded,
    onToggle,
    drill,
    renderRow,
    rowKey,
}: {
    id: string;
    label: string;
    count: number;
    expanded: boolean;
    onToggle: () => void;
    drill: DrillResult<T>;
    renderRow: (row: T) => React.ReactNode;
    rowKey: (row: T) => React.Key;
}) {
    const { t } = useLanguage();
    const bodyId = `${id}-body`;
    const remaining = drill.total - drill.items.length;

    return (
        <li className={styles.accordionItem}>
            <button
                type="button"
                className={styles.accordionHeader}
                aria-expanded={expanded}
                aria-controls={bodyId}
                onClick={onToggle}
            >
                <Count value={count} />
                <span className={styles.accordionLabel}>{label}</span>
                <Chevron open={expanded} />
            </button>
            {expanded && (
                <div id={bodyId} className={styles.accordionBody} role="region">
                    {drill.isLoading ? (
                        <div className={styles.drillSpinnerRow}>
                            <span className={styles.drillSpinner} aria-hidden="true" />
                            <span>{label}</span>
                        </div>
                    ) : drill.isError ? (
                        <div className={styles.drillError} role="alert">
                            <span>{t('superadmin.categories.impact.loadError')}</span>
                            <button
                                type="button"
                                className={styles.drillRetry}
                                onClick={() => drill.refetch()}
                            >
                                {t('superadmin.categories.retry')}
                            </button>
                        </div>
                    ) : drill.items.length === 0 ? (
                        <p className={styles.drillEmpty}>
                            {t('superadmin.categories.impact.empty')}
                        </p>
                    ) : (
                        <>
                            <ul className={styles.drillList}>
                                {drill.items.map((row) => (
                                    <li key={rowKey(row)} className={styles.drillRow}>
                                        {renderRow(row)}
                                    </li>
                                ))}
                            </ul>
                            {drill.hasNextPage && (
                                <button
                                    type="button"
                                    className={styles.drillLoadMore}
                                    onClick={() => drill.fetchNextPage()}
                                    disabled={drill.isFetchingNextPage}
                                >
                                    {t('superadmin.categories.impact.more').replace(
                                        '{count}',
                                        String(Math.max(remaining, 0)),
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </li>
    );
}

const DeactivateCategoryModal: React.FC<DeactivateCategoryModalProps> = ({
    open,
    onClose,
    categoryId,
    categoryName,
}) => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    const deactivateMutation = useDeactivateCategory();

    const { data: impact, isLoading, isError, refetch, isFetching } =
        useCategoryImpact(open ? categoryId : null);

    const [expanded, setExpanded] = useState<{
        salons: boolean;
        services: boolean;
        bookings: boolean;
    }>({ salons: false, services: false, bookings: false });

    const salonsQuery = useCategoryImpactSalons(
        open ? categoryId : null,
        expanded.salons,
    );
    const servicesQuery = useCategoryImpactServices(
        open ? categoryId : null,
        expanded.services,
    );
    const bookingsQuery = useCategoryImpactBookings(
        open ? categoryId : null,
        expanded.bookings,
    );

    const onConfirm = async () => {
        if (categoryId == null) return;
        try {
            await deactivateMutation.mutateAsync(categoryId);
            showToast(t('superadmin.categories.toast.deactivated.done'), 'success');
            onClose();
        } catch {
            showToast(t('superadmin.categories.toast.error'), 'error');
        }
    };

    if (!open) return null;

    const hasSingleCategory = !!impact && impact.singleCategorySalons > 0;

    const flatten = <T,>(q: {
        data?: { pages: { items: T[]; total: number }[] };
    }): { items: T[]; total: number } => {
        const pages = q.data?.pages ?? [];
        return {
            items: pages.flatMap((p) => p.items),
            total: pages[0]?.total ?? 0,
        };
    };

    const salons = flatten<CategoryImpactSalonRow>(salonsQuery);
    const services = flatten<CategoryImpactServiceRow>(servicesQuery);
    const bookings = flatten<CategoryImpactBookingRow>(bookingsQuery);

    // Locale-aware date+time for PDPL booking rows (wrapped LTR in the row).
    // AB#2052 — the appointment datetime is the salon's wall-clock stamped
    // `…Z`; format it in UTC so it is not shifted to the viewer's timezone.
    const fmtDateTime = (iso: string): string =>
        formatAppointmentDateTime(iso, language, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    return (
        <div className={styles.modalOverlay} role="presentation" onClick={onClose}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="deactivate-category-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 id="deactivate-category-title" className={styles.modalTitle}>
                        {t('superadmin.categories.deactivate.title')}
                    </h2>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={t('superadmin.categories.action.cancel')}
                    >
                        &times;
                    </button>
                </div>

                <div className={styles.modalContent}>
                    {categoryName && (
                        <p className={styles.destructiveLead}>
                            <strong>{categoryName}</strong>
                        </p>
                    )}

                    {isLoading ? (
                        <div aria-busy="true">
                            <div className={styles.impactSkeleton} />
                            <div className={styles.impactSkeleton} />
                            <div className={styles.impactSkeleton} />
                        </div>
                    ) : isError ? (
                        <div className={styles.errorBanner} role="alert">
                            <span>{t('superadmin.categories.deactivate.impactError')}</span>
                            <Button
                                label={t('superadmin.categories.retry')}
                                size="small"
                                variant="secondary"
                                onClick={() => refetch()}
                                disabled={isFetching}
                                type="button"
                            />
                        </div>
                    ) : impact ? (
                        <>
                            <ul className={styles.accordionList}>
                                <ImpactAccordion<CategoryImpactSalonRow>
                                    id="impact-salons"
                                    label={t('superadmin.categories.impact.salons')}
                                    count={impact.salonsSubscribed}
                                    expanded={expanded.salons}
                                    onToggle={() =>
                                        setExpanded((s) => ({ ...s, salons: !s.salons }))
                                    }
                                    drill={{
                                        items: salons.items,
                                        total: salons.total,
                                        isLoading: salonsQuery.isLoading,
                                        isError: salonsQuery.isError,
                                        hasNextPage: !!salonsQuery.hasNextPage,
                                        isFetchingNextPage: salonsQuery.isFetchingNextPage,
                                        fetchNextPage: salonsQuery.fetchNextPage,
                                        refetch: salonsQuery.refetch,
                                    }}
                                    rowKey={(r) => r.businessId}
                                    renderRow={(r) => (
                                        <>
                                            <span className={styles.drillPrimary}>{r.name}</span>
                                            <span className={styles.drillSecondary}>{r.city}</span>
                                            {r.isSingleCategory && (
                                                <span className={styles.drillOnlyCategory}>
                                                    {t('superadmin.categories.impact.onlyCategory')}
                                                </span>
                                            )}
                                        </>
                                    )}
                                />

                                <ImpactAccordion<CategoryImpactServiceRow>
                                    id="impact-services"
                                    label={t('superadmin.categories.impact.services')}
                                    count={impact.servicesCount}
                                    expanded={expanded.services}
                                    onToggle={() =>
                                        setExpanded((s) => ({ ...s, services: !s.services }))
                                    }
                                    drill={{
                                        items: services.items,
                                        total: services.total,
                                        isLoading: servicesQuery.isLoading,
                                        isError: servicesQuery.isError,
                                        hasNextPage: !!servicesQuery.hasNextPage,
                                        isFetchingNextPage: servicesQuery.isFetchingNextPage,
                                        fetchNextPage: servicesQuery.fetchNextPage,
                                        refetch: servicesQuery.refetch,
                                    }}
                                    rowKey={(r) => r.serviceId}
                                    renderRow={(r) => (
                                        <>
                                            <span className={styles.drillPrimary}>{r.name}</span>
                                            <span className={styles.drillSecondary}>
                                                {r.salonName}
                                            </span>
                                            {r.fromPrice != null && r.currency && (
                                                <span className={styles.drillSecondary} dir="ltr">
                                                    {t(
                                                        'superadmin.categories.impact.serviceFrom',
                                                    )
                                                        .replace(
                                                            '{price}',
                                                            r.fromPrice.toFixed(2),
                                                        )
                                                        .replace('{currency}', r.currency)}
                                                </span>
                                            )}
                                        </>
                                    )}
                                />

                                <ImpactAccordion<CategoryImpactBookingRow>
                                    id="impact-bookings"
                                    label={t('superadmin.categories.impact.bookings')}
                                    count={impact.upcomingAppointments}
                                    expanded={expanded.bookings}
                                    onToggle={() =>
                                        setExpanded((s) => ({ ...s, bookings: !s.bookings }))
                                    }
                                    drill={{
                                        items: bookings.items,
                                        total: bookings.total,
                                        isLoading: bookingsQuery.isLoading,
                                        isError: bookingsQuery.isError,
                                        hasNextPage: !!bookingsQuery.hasNextPage,
                                        isFetchingNextPage: bookingsQuery.isFetchingNextPage,
                                        fetchNextPage: bookingsQuery.fetchNextPage,
                                        refetch: bookingsQuery.refetch,
                                    }}
                                    rowKey={(r) =>
                                        `${r.appointmentDateTimeUtc}-${r.serviceName}-${r.salonName}`
                                    }
                                    renderRow={(r) => (
                                        // PDPL: date+time (LTR isolate) · service · salon ONLY.
                                        <>
                                            <span className={styles.drillPrimary} dir="ltr">
                                                {fmtDateTime(r.appointmentDateTimeUtc)}
                                            </span>
                                            <span className={styles.drillSecondary}>
                                                {r.serviceName}
                                            </span>
                                            <span className={styles.drillSecondary}>
                                                {r.salonName}
                                            </span>
                                        </>
                                    )}
                                />
                            </ul>

                            {/* Separate, visually distinct single-category warning. */}
                            {hasSingleCategory && (
                                <p className={styles.singleCategoryWarning} role="alert">
                                    {t(
                                        'superadmin.categories.deactivate.singleCategoryWarning',
                                    )
                                        .replace('{count}', String(impact.singleCategorySalons))
                                        .replace('{total}', String(impact.salonsSubscribed))}
                                </p>
                            )}

                            {/* Reversibility / consequences copy (weight preservation). */}
                            <p className={styles.notice}>
                                {t('superadmin.categories.deactivate.body')}
                            </p>
                        </>
                    ) : null}
                </div>

                <div className={styles.modalActions}>
                    <Button
                        label={t('superadmin.categories.action.cancel')}
                        variant="secondary"
                        onClick={onClose}
                        disabled={deactivateMutation.isPending}
                        type="button"
                    />
                    <Button
                        label={t('superadmin.categories.deactivate.confirm')}
                        // Destructive-red primary (weight preservation, decision 6).
                        backgroundColor="var(--destructive-fill)"
                        fontColor="var(--on-destructive)"
                        // Block confirm until counts are visible.
                        disabled={
                            deactivateMutation.isPending || isLoading || isError || !impact
                        }
                        onClick={onConfirm}
                        type="button"
                    />
                </div>
            </div>
        </div>
    );
};

export default DeactivateCategoryModal;
